import "./QuestionCard.scss";

import { Button, Flex, Menu, MoreIcon, Tooltip } from "@fluentui/react-northstar";
import { FieldDescriptors } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import {
  createNominalQuestion,
  createOpenEndedQuestion,
  createOrdinalQuestion,
  randomString,
} from "@teams/client-shared";
import {
  NominalQuestion,
  NominalQuestionChoice,
  OpenEndedQuestion,
  OrdinalQuestion,
  OrdinalQuestionChoice,
  Question,
  QuestionTemplate,
  QuestionType,
} from "@teams/types";
import React, { useCallback } from "react";

import { PollyIcon } from "../../../../../_shared/components/PollyIcon";
import { AuthoringFields } from "../types";

export const QuestionCardMenu: React.FC<{
  compressed?: boolean;
  isLimitedEdit?: boolean;
  fields: FieldDescriptors<AuthoringFields>;
  question: FieldDescriptors<Question<QuestionType>>;
  questionTemplates: QuestionTemplate[];
  addQuestion: (
    fields: FieldDescriptors<AuthoringFields>,
    question: Question<QuestionType>
  ) => void;
}> = ({ compressed, isLimitedEdit, fields, question, questionTemplates, addQuestion }) => {
  const duplicateQuestion = useCallback(() => {
    switch (question.questionType.value) {
      case "nominal":
        const nominalDescriptors = question as FieldDescriptors<NominalQuestion>;
        const nominal = createNominalQuestion({
          label: question.label.value,
          choices: nominalDescriptors.choices.value.map<NominalQuestionChoice>(choice => ({
            id: randomString(),
            value: choice.value,
            correct: choice.correct,
          })),
          allowComments: !!question.allowComments?.value,
          commentAppeal: question.commentAppeal?.value,
          required: question.required.value,
          templateId: question.templateId?.value || undefined,
        });

        addQuestion(fields, nominal);
        break;
      case "ordinal":
        const ordinalDescriptors = question as FieldDescriptors<OrdinalQuestion>;
        const ordinal = createOrdinalQuestion(
          {
            label: question.label.value,
            choices: question.templateId
              ? ordinalDescriptors.choices.value.map<OrdinalQuestionChoice>(choice => ({
                  id: randomString(),
                  label: choice.label,
                  value: choice.value,
                }))
              : undefined,
            allowComments: !!question.allowComments?.value,
            commentAppeal: question.commentAppeal?.value,
            required: question.required.value,
          },
          undefined,
          question.templateId?.value
            ? questionTemplates.find(t => t.id === question.templateId?.value)
            : undefined
        );

        addQuestion(fields, ordinal);
        break;
      case "open-ended":
        const openEndedDescriptors = question as FieldDescriptors<OpenEndedQuestion>;
        const openEnded = createOpenEndedQuestion({
          label: openEndedDescriptors.label.value,
          required: openEndedDescriptors.required.value,
          contentType: openEndedDescriptors.contentType.value,
        });

        addQuestion(fields, openEnded);
        break;
    }
  }, [addQuestion, question, fields, questionTemplates]);

  const onDeleteQuestion = useCallback(
    (fields: FieldDescriptors<AuthoringFields>, questionId: string) => {
      const toDeleteIndex = fields.questions.value.findIndex(q => q.id === questionId);

      if (toDeleteIndex !== -1) {
        const questions = fields.questions.value.filter((_q, i) => toDeleteIndex !== i);

        fields.questions.onChange(questions);

        if (questions.length === 1 && fields.type.value === "survey") {
          fields.type.onChange("poll");
        }
      }
    },
    []
  );

  return (
    <div>
      {!compressed && (
        <Flex>
          {!isLimitedEdit && (
            <Tooltip content="Duplicate question">
              <Button
                iconOnly
                text
                icon={<PollyIcon icon="duplicate-icon" inheritColor />}
                disabled={fields.questions.value.length >= SurveyLimits.Questions.Max}
                onClick={() => duplicateQuestion()}
              />
            </Tooltip>
          )}

          {fields.questions.value.length > 1 && !isLimitedEdit && (
            <Tooltip content="Delete question">
              <Button
                className="trash-icon-button"
                iconOnly
                text
                icon={<PollyIcon icon="trash-icon" inheritColor />}
                onClick={() => onDeleteQuestion(fields, question.id.value)}
              />
            </Tooltip>
          )}
        </Flex>
      )}

      {compressed && (
        <Menu
          iconOnly
          items={[
            {
              key: "overflow",
              icon: <MoreIcon />,
              indicator: false,
              menu: {
                items: [
                  {
                    key: "dupe",
                    icon: <PollyIcon icon="duplicate-icon" inheritColor />,
                    content: "Duplicate",
                    onClick: () => duplicateQuestion(),
                  },
                  ...(fields.questions.value.length > 1
                    ? [
                        {
                          key: "delete",
                          icon: <PollyIcon icon="trash-icon" inheritColor />,
                          content: "Delete",
                          onClick: () => onDeleteQuestion(fields, question.id.value),
                        },
                      ]
                    : []),
                ],
              },
            },
          ]}
        />
      )}
    </div>
  );
};
